import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  checkGameProfileVerification,
  loadCurrentGameProfile,
  loadLatestVerifiedChallengeId,
  startCurrentGameProfileReverification,
  startGameProfileVerification,
} from '../api/gameProfileVerificationApi.js';
import { syncPersonalNikkeData } from '../../personal-nikke/api/personalNikkeApi.js';

const EMPTY_NOTICE = { type: '', message: '' };
const AUTO_CHECK_DURATION_MS = 150 * 1000;
const AUTO_CHECK_INTERVAL_MS = 5 * 1000;
const RETRYABLE_CHECK_CODES = new Set([
  'blablalink_timeout',
  'blablalink_network_error',
  'public_profile_unavailable',
  'game_profile_details_unavailable',
]);

function calculateRemainingSeconds(expiresAt) {
  if (!expiresAt) return 0;
  return Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 1000));
}

function formatRemaining(seconds) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`;
}

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function profileUrlFromProfile(profile) {
  const rawOpenid = String(profile?.openid || '').trim();
  if (!rawOpenid) return '';

  let pageOpenid = rawOpenid;
  if (/^\d+-\d+$/.test(rawOpenid)) {
    try {
      pageOpenid = btoa(rawOpenid);
    } catch {
      pageOpenid = rawOpenid;
    }
  }

  return `https://www.blablalink.com/user?openid=${encodeURIComponent(pageOpenid)}`;
}

export function useGameProfileVerificationStart() {
  const [profileUrl, setProfileUrl] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(EMPTY_NOTICE);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const [checking, setChecking] = useState(false);
  const [checkNotice, setCheckNotice] = useState(EMPTY_NOTICE);
  const [checkDeadline, setCheckDeadline] = useState(0);
  const [checkRemainingSeconds, setCheckRemainingSeconds] = useState(0);
  const [checkAttemptCount, setCheckAttemptCount] = useState(0);
  const [verifiedProfile, setVerifiedProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const verificationRunRef = useRef(0);
  const savedProfileRef = useRef(null);

  useEffect(() => {
    let active = true;

    const loadVerifiedProfile = async () => {
      setProfileLoading(true);

      try {
        const currentProfile = await loadCurrentGameProfile();
        if (!active) return;

        if (!currentProfile) {
          savedProfileRef.current = null;
          setVerifiedProfile(null);
          setProfileUrl('');
          return;
        }

        savedProfileRef.current = currentProfile;
        setVerifiedProfile(currentProfile);
        setProfileUrl(profileUrlFromProfile(currentProfile));

        if (!currentProfile.guildName) {
          try {
            const verifiedChallengeId = await loadLatestVerifiedChallengeId();
            if (!active || !verifiedChallengeId) return;

            const refreshed = await checkGameProfileVerification(verifiedChallengeId);
            if (active && refreshed.status === 'verified' && refreshed.profile) {
              savedProfileRef.current = refreshed.profile;
              setVerifiedProfile(refreshed.profile);
              setProfileUrl(profileUrlFromProfile(refreshed.profile));
            }
          } catch (error) {
            if (active) {
              setCheckNotice({
                type: 'error',
                message: error?.message || '유니온 최신 정보를 불러오지 못했습니다.',
              });
            }
          }
        }
      } catch (error) {
        if (active) {
          setCheckNotice({
            type: 'error',
            message: error?.message || '연결된 게임 프로필을 불러오지 못했습니다.',
          });
        }
      } finally {
        if (active) setProfileLoading(false);
      }
    };

    loadVerifiedProfile();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!challenge?.expiresAt) {
      setRemainingSeconds(0);
      return undefined;
    }

    const updateRemaining = () => {
      setRemainingSeconds(calculateRemainingSeconds(challenge.expiresAt));
    };

    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);

    return () => window.clearInterval(timer);
  }, [challenge?.expiresAt]);

  useEffect(() => {
    if (!checking || !checkDeadline) {
      setCheckRemainingSeconds(0);
      return undefined;
    }

    const updateRemaining = () => {
      setCheckRemainingSeconds(
        Math.max(0, Math.ceil((checkDeadline - Date.now()) / 1000)),
      );
    };

    updateRemaining();
    const timer = window.setInterval(updateRemaining, 1000);
    return () => window.clearInterval(timer);
  }, [checkDeadline, checking]);

  useEffect(() => () => {
    verificationRunRef.current += 1;
  }, []);

  const expired = Boolean(challenge && remainingSeconds <= 0);
  const formattedRemaining = useMemo(
    () => formatRemaining(remainingSeconds),
    [remainingSeconds],
  );
  const formattedCheckRemaining = useMemo(
    () => formatRemaining(checkRemainingSeconds),
    [checkRemainingSeconds],
  );
  const hasSavedProfile = Boolean(verifiedProfile?.openid || savedProfileRef.current?.openid);

  const prepareChallengeStart = useCallback(() => {
    verificationRunRef.current += 1;
    setSubmitting(true);
    setNotice(EMPTY_NOTICE);
    setCheckNotice(EMPTY_NOTICE);
    setChecking(false);
    setCheckDeadline(0);
    setCheckAttemptCount(0);
  }, []);

  const applyChallenge = useCallback((nextChallenge) => {
    setChallenge(nextChallenge);
    setVerifiedProfile(null);
    setNotice({
      type: 'success',
      message: '인증 코드가 발급되었습니다. BlablaLink 상태 메시지에 입력해주세요.',
    });
  }, []);

  const submit = useCallback(async (event) => {
    event.preventDefault();

    if (!profileUrl.trim()) {
      setNotice({ type: 'error', message: 'BlablaLink 프로필 URL을 입력해주세요.' });
      return;
    }

    prepareChallengeStart();
    try {
      const nextChallenge = await startGameProfileVerification(profileUrl);
      savedProfileRef.current = null;
      applyChallenge(nextChallenge);
    } catch (error) {
      setChallenge(null);
      setNotice({ type: 'error', message: error?.message || '인증 코드를 발급하지 못했습니다.' });
    } finally {
      setSubmitting(false);
    }
  }, [applyChallenge, prepareChallengeStart, profileUrl]);

  const reverifyCurrentProfile = useCallback(async () => {
    const sourceProfile = verifiedProfile || savedProfileRef.current;
    if (!sourceProfile?.openid) {
      setNotice({ type: 'error', message: '저장된 BlablaLink 프로필을 찾지 못했습니다.' });
      return;
    }

    savedProfileRef.current = sourceProfile;
    prepareChallengeStart();
    try {
      const nextChallenge = await startCurrentGameProfileReverification();
      applyChallenge(nextChallenge);
    } catch (error) {
      setVerifiedProfile(sourceProfile);
      setProfileUrl(profileUrlFromProfile(sourceProfile));
      setChallenge(null);
      setCheckNotice({ type: 'error', message: error?.message || '재인증 코드를 발급하지 못했습니다.' });
    } finally {
      setSubmitting(false);
    }
  }, [applyChallenge, prepareChallengeStart, verifiedProfile]);

  const startChecking = useCallback(async () => {
    if (!challenge?.id) {
      setCheckNotice({ type: 'error', message: '먼저 인증 코드를 발급해주세요.' });
      return;
    }

    if (expired) {
      setCheckNotice({ type: 'error', message: '인증 코드가 만료되었습니다. 새 코드를 발급해주세요.' });
      return;
    }

    const runId = verificationRunRef.current + 1;
    verificationRunRef.current = runId;

    const challengeDeadline = new Date(challenge.expiresAt).getTime();
    const deadline = Math.min(Date.now() + AUTO_CHECK_DURATION_MS, challengeDeadline);

    setChecking(true);
    setCheckDeadline(deadline);
    setCheckAttemptCount(0);
    setCheckNotice({
      type: 'checking',
      message: 'BlablaLink 불러오는 중… 상태 메시지 반영을 기다리고 있습니다.',
    });

    while (verificationRunRef.current === runId && Date.now() < deadline) {
      try {
        const result = await checkGameProfileVerification(challenge.id);

        if (verificationRunRef.current !== runId) return;

        if (result.status === 'verified') {
          savedProfileRef.current = result.profile;
          setVerifiedProfile(result.profile);
          setProfileUrl(profileUrlFromProfile(result.profile));
          setChallenge(null);
          setCheckNotice({
            type: 'checking',
            message: '게임 프로필 인증 완료 · 내 니케 정보를 동기화하고 있습니다…',
          });
          try {
            const syncResult = await syncPersonalNikkeData();
            window.dispatchEvent(new CustomEvent('dororaid:personal-nikke-changed'));
            setCheckNotice({
              type: 'success',
              message: `BlablaLink 인증과 니케 ${Number(syncResult.ownedCount || 0).toLocaleString('ko-KR')}명 동기화가 완료되었습니다.`,
            });
          } catch (syncError) {
            setCheckNotice({
              type: 'success',
              message: `게임 프로필 인증은 완료되었습니다. 니케 정보는 내 니케 화면에서 다시 동기화할 수 있습니다. (${syncError?.message || '동기화 지연'})`,
            });
          }
          setChecking(false);
          setCheckDeadline(0);
          return;
        }

        setCheckAttemptCount(result.attemptCount || 0);
        setCheckNotice({
          type: 'checking',
          message: 'BlablaLink 불러오는 중… 아직 상태 메시지에서 인증 코드를 찾지 못했습니다.',
        });
      } catch (error) {
        if (verificationRunRef.current !== runId) return;

        if (!RETRYABLE_CHECK_CODES.has(error?.code)) {
          setCheckNotice({
            type: 'error',
            message: error?.message || '게임 프로필 인증 확인에 실패했습니다.',
          });
          setChecking(false);
          setCheckDeadline(0);
          return;
        }

        setCheckNotice({
          type: 'checking',
          message: `${error.message} 자동으로 다시 확인하고 있습니다.`,
        });
      }

      const waitTime = Math.min(AUTO_CHECK_INTERVAL_MS, Math.max(0, deadline - Date.now()));
      if (waitTime <= 0) break;
      await wait(waitTime);
    }

    if (verificationRunRef.current === runId) {
      setChecking(false);
      setCheckDeadline(0);
      setCheckNotice({
        type: 'error',
        message: '2분 30초 동안 인증 코드를 찾지 못했습니다. 상태 메시지 저장 여부를 확인한 뒤 다시 눌러주세요.',
      });
    }
  }, [challenge?.expiresAt, challenge?.id, expired]);

  const cancelChecking = useCallback(() => {
    verificationRunRef.current += 1;
    setChecking(false);
    setCheckDeadline(0);
    setCheckNotice({ type: '', message: '자동 확인을 중지했습니다.' });
  }, []);

  const resetChallenge = useCallback(() => {
    verificationRunRef.current += 1;
    setChallenge(null);
    setNotice(EMPTY_NOTICE);
    setCheckNotice(EMPTY_NOTICE);
    setChecking(false);
    setCheckDeadline(0);
    setCheckAttemptCount(0);

    const savedProfile = savedProfileRef.current;
    setVerifiedProfile(savedProfile);
    setProfileUrl(profileUrlFromProfile(savedProfile));
  }, []);

  return {
    profileUrl,
    challenge,
    submitting,
    notice,
    remainingSeconds,
    formattedRemaining,
    expired,
    checking,
    checkNotice,
    checkAttemptCount,
    checkRemainingSeconds,
    formattedCheckRemaining,
    verifiedProfile,
    profileLoading,
    hasSavedProfile,
    setProfileUrl,
    submit,
    reverifyCurrentProfile,
    startChecking,
    cancelChecking,
    resetChallenge,
  };
}
